import { Component, Inject, OnInit } from '@angular/core';
import { StateService, TransitionService } from '@uirouter/angular';
import { Transition } from '@uirouter/core';
import { ListService } from '@app/common/services/list.service';

import { AuthService } from '@app/common/services/auth.service';
import { TabModel } from '@app/common/models/tab.model';
import { DocumentModel } from '@app/common/models/document.model';
import { NoteModel } from '@app/common/models/note.model';
import { ProjectPropertiesGroupModel } from '@app/ps/models/project-properties-group.model';
import { ListModel } from '@app/common/models/list.model';
import { UserNamePipe } from '@app/common/pipes/username.pipe';
import { ModulesService } from '@app/common/services/modules.service';
import { BrandTranslatePipe } from '@app/common/pipes/brand-translate.pipe';
import { APP_BRAND, APPLICATIONS } from '@app/common/services/config.service';
import { HelpService } from '@app/common/services/help.service';
import { Restangular } from 'ngx-restangular';
import { RESTANGULAR_SETTINGS } from '@app/common/services/restangular-settings.service';
import { DialogService } from '@app/common/services/dialog.service';
import { RESTANGULAR_SYMAP } from '@app/common/services/restangular-symap.service';
import { IsprofondPipe } from '@app/ps/pipes/isprofond.pipe';
import {StringUtils} from "@app/common/utils/string.utils";
import {SettingsService} from "@app/ps/services/settings.service";
import {BrandService} from "@app/common/services/brand.service";
import {AttachmentModel} from "@app/common/models/attachmentModel";

@Component({
  selector: 'project-info',
  templateUrl: './project-info.component.html',
  styleUrls: ['./project-info.component.scss']
})
export class ProjectInfoComponent implements OnInit {
  loaded = false;
  project: any;
  settings: any;
  noteList: ListModel<NoteModel>;
  historyList: ListModel<NoteModel>;
  landTakeDocumentationNoteList: ListModel<NoteModel>;
  attachmentList: ListModel<AttachmentModel>;
  documentList: ListModel<DocumentModel>;
  tab: string;
  documentTab = 'documents-epd__documents';
  tabs: TabModel[];
  usersTemp: any = {};
  // naplní se dynamicky podle načtených vlastností (přeházečka "jiná osoba" pro každého zpracovatele), aby počet zpracovatelů byl jen v konfiguraci na backendu
  customGroupsVisibility: string[] = [];
  // opakovatelné bloky (zpracovatel/schvalovatel č. N), které se zobrazují/skrývají jako celek
  private readonly sectionConfigs: { type: string; headerRegex: RegExp; entityLabel: string }[] = [
    { type: 'zpracovatel', headerRegex: /^Zpracovatel majetkoprávní činnosti č\. (\d+)/, entityLabel: 'zpracovatele' },
    { type: 'schvalovatel', headerRegex: /^Schvalovatel č\. (\d+)/, entityLabel: 'schvalovatele' },
  ];
  // počet aktuálně zobrazených bloků daného typu v dané skupině, klíč viz getSectionKey
  visibleSectionsCount: { [key: string]: number } = {};
  projectProperties: ProjectPropertiesGroupModel[];
  helpIds = HelpService.HELP_IDS;
  private additionalInfoChanged = false;
  private deregisterLeaveListener: Function;
  additionalInfoCollapsed: { [key: string]: boolean } = {
    'Údaje k akci': false,
    'Obecné údaje do smlouvy': true,
    'Údaje do nájemní smlouvy / průvodního dopisu': true,
    'Údaje do průvodního dopisu': true,
    'Dokument finanční kontroly / Referátník': true,
  };
  public areas;
  public newPriceAssessment = null;
  public assessments: any[];
  projectFolder;
  documentTabs: TabModel[] = [
    {
      name: 'Obsah dokumentů EPD',
      id: 'documents-epd__documents',
    },
    {
      name: 'Seznam komponent EPD',
      id: 'documents-epd__components',
    },
  ];
  kvsReferentsFilter = [];

  constructor(
    @Inject(APP_BRAND) public APP_BRAND: any,
    @Inject(RESTANGULAR_SETTINGS) public restangularSettings: any,
    @Inject(APPLICATIONS) public APPLICATIONS: any,
    @Inject(RESTANGULAR_SYMAP) public restangularSymap: any,
    private restangular: Restangular,
    public authService: AuthService,
    public userNamePipe: UserNamePipe,
    private stateService: StateService,
    private listService: ListService,
    private modulesService: ModulesService,
    private brandTranslatePipe: BrandTranslatePipe,
    private dialogService: DialogService,
    private transitionService: TransitionService,
    private settingsService: SettingsService,
    private brandService: BrandService,
  ) {
    this.project = authService.getActualProject();
    this.uiOnParamsChanged(this.stateService.params);
    this.isActiveTab = this.isActiveTab.bind(this);
    this.isActiveDocumentTab = this.isActiveDocumentTab.bind(this);
    this.saveProperties = this.saveProperties.bind(this);
    this.onUpdateDocumentList = this.onUpdateDocumentList.bind(this);
    this.onUpdateNoteList = this.onUpdateNoteList.bind(this);
    this.onUpdateHistoryList = this.onUpdateHistoryList.bind(this);
    this.onAdditionalInfoChanged = this.onAdditionalInfoChanged.bind(this);

    this.onSaveAssessment = this.onSaveAssessment.bind(this);
    this.onCancelNewAssessment = this.onCancelNewAssessment.bind(this);
    this.onNewAssessment = this.onNewAssessment.bind(this);
    this.onUpdateAssessment = this.onUpdateAssessment.bind(this);
  }

  async ngOnInit() {
    await this.loadProperties();
    await this.loadSettings();
    await this.loadProjectFolder();
    document.title = this.brandTranslatePipe.transform('ps.projectInfo');
    this.tabs = this.getTabs();
    this.loadNotes();
    this.loadHistory();
    this.loadLandTakeDocumentationNotes();
    this.loadAttachments();
    if (!this.brandService.isVisible('mpv')) {
      this.loadAreas();
      this.loadAssessments();
    } else {
      this.loadDocuments();
    }
    this.loaded = true;
  }

  async loadProperties() {
    this.projectProperties = await this.restangular.all('properties').customGET('').toPromise();
    await this.prepareProperties();
  }

  // (znovu) sestaví metadata sekcí (používaná pro sbalování), přepínače viditelnosti a uložené popisky uživatelů pro aktuálně načtené vlastnosti
  private async prepareProperties() {
    const customGroupsVisibility = new Set<string>();
    for (const group of this.projectProperties) {
      this.annotateSections(group);
      this.initVisibleSectionsCount(group);
      for (const item of group.items) {
        if (/^podepisujici\d+JinaOsoba$/.test(item.templateVariable)) {
          customGroupsVisibility.add(item.templateVariable);
        }
        if (item.type === 'USER_SELECT' && item.valueLong) {
          this.usersTemp[item.templateVariable] = await this.modulesService.getUser(item.valueLong).then(d => d.plain());
        }
      }
    }
    this.customGroupsVisibility = Array.from(customGroupsVisibility);
  }

  // ke každé položce ve skupině přiřadí typ a číslo bloku podle hlavičky, pod kterou se nachází
  private annotateSections(group: any) {
    let current: { type: string; number: number } = null;
    for (const item of group.items) {
      if (!item.type) {
        current = this.matchSectionHeader(item.name);
      }
      if (current) {
        item.sectionType = current.type;
        item.sectionNumber = current.number;
      }
    }
  }

  // zjistí, zda název položky odpovídá některé z hlaviček bloků, a pokud ano, vrátí její typ a číslo
  private matchSectionHeader(name: string): { type: string; number: number } | null {
    for (const config of this.sectionConfigs) {
      const match = config.headerRegex.exec(name);
      if (match) {
        return { type: config.type, number: parseInt(match[1]) };
      }
    }
    return null;
  }

  private hasValue(item: any): boolean {
    return !!(item.valueText || item.valueLong || item.valueDate || item.valueBoolean);
  }

  // na začátku zobrazí jen vyplněné bloky (a alespoň první), zbytek si uživatel přidá tlačítkem "+"
  private initVisibleSectionsCount(group: any) {
    const maxFilledBySectionType: { [type: string]: number } = {};
    for (const item of group.items) {
      if (item.sectionType && this.hasValue(item)) {
        maxFilledBySectionType[item.sectionType] = Math.max(maxFilledBySectionType[item.sectionType] || 0, item.sectionNumber);
      }
    }
    for (const type of Object.keys(maxFilledBySectionType)) {
      this.setVisibleSectionsCount(group, type, Math.max(1, maxFilledBySectionType[type]));
    }
  }

  // unikátní klíč pro počet zobrazených bloků daného typu v rámci skupiny
  private getSectionKey(group: any, type: string): string {
    return group.groupName + '__' + type;
  }

  private getVisibleSectionsCount(group: any, type: string): number {
    return this.visibleSectionsCount[this.getSectionKey(group, type)] || 1;
  }

  private setVisibleSectionsCount(group: any, type: string, count: number) {
    this.visibleSectionsCount[this.getSectionKey(group, type)] = count;
  }

  // true, pokud položka nepatří do žádného bloku, nebo patří do aktuálně zobrazeného bloku
  isSectionVisible(group: any, item: any): boolean {
    if (!item.sectionType) {
      return true;
    }
    return item.sectionNumber <= this.getVisibleSectionsCount(group, item.sectionType);
  }

  // typy opakovatelných bloků přítomné ve skupině (pro vykreslení tlačítek přidat/odebrat)
  getSectionTypes(group: any): string[] {
    return Array.from(new Set<string>(group.items.map(item => item.sectionType).filter(type => !!type)));
  }

  // lze přidat další blok, pokud v šabloně existuje ještě neukázaný blok daného typu
  canAddSection(group: any, type: string): boolean {
    const maxNumber = group.items.reduce((max, item) => item.sectionType === type ? Math.max(max, item.sectionNumber) : max, 0);
    return this.getVisibleSectionsCount(group, type) < maxNumber;
  }

  addSection(group: any, type: string) {
    this.setVisibleSectionsCount(group, type, this.getVisibleSectionsCount(group, type) + 1);
  }

  // odebrat lze jen tehdy, když je zobrazen víc než jeden blok daného typu
  canRemoveSection(group: any, type: string): boolean {
    return this.getVisibleSectionsCount(group, type) > 1;
  }

  // po potvrzení smaže hodnoty posledního bloku daného typu a skryje ho
  removeSection(group: any, type: string) {
    const lastNumber = this.getVisibleSectionsCount(group, type);
    if (lastNumber <= 1) {
      return;
    }
    this.dialogService.confirmDialogPromise(`Opravdu chcete odebrat posledního ${this.sectionEntityLabel(type)}? Vyplněné údaje budou vymazány.`).then((confirmed) => {
      if (confirmed !== true) {
        return;
      }
      for (const item of group.items) {
        if (item.sectionType === type && item.sectionNumber === lastNumber) {
          item.valueText = null;
          item.valueLong = null;
          item.valueDate = null;
          item.valueBoolean = null;
        }
      }
      this.setVisibleSectionsCount(group, type, lastNumber - 1);
      this.onAdditionalInfoChanged();
    });
  }

  sectionAddLabel(type: string): string {
    return `Přidat dalšího ${this.sectionEntityLabel(type)}`;
  }

  sectionRemoveLabel(type: string): string {
    return `Odebrat posledního ${this.sectionEntityLabel(type)}`;
  }

  // tvar slova v 2. pádu (koho/čeho) pro použití v textech tlačítek a potvrzovacího dialogu
  private sectionEntityLabel(type: string): string {
    const config = this.sectionConfigs.find(c => c.type === type);
    return config ? config.entityLabel : type;
  }

  async loadSettings() {
    this.settingsService.clearSettings();
    return  this.settingsService.loadSettings(this.project.id).then(s => {
      this.settings = this.settingsService.getSettings();
      this.settings.kvsReferents = this.settings.kvsReferents || [];
      this.onKvsReferentsChanged(0, {data: this.settings.kvsReferents});
    });
  }

  reloadKvsReferentsRegister = [];
  reloadKvsReferents = [];

  onKvsReferentsChanged(i, event) {
    // odfiltrovat smazané
    this.settings.kvsReferents = event.data.filter(item => item);
    // nedovolit zvolit opakovaně
    [...this.settings.kvsReferents.keys(), this.settings.kvsReferents.length].forEach(i1 => {
      this.kvsReferentsFilter[i1]=this.kvsReferentsFilter[i1] || {
       filters: {
        ssoCompanies: { values: [ 'ŘSD' ] },
        entityId: { values: [], negation: true }
       },
       sortOrder: [
         { sortBy: 'surname' },
         { sortBy: 'name' },
       ],
      };
      this.kvsReferentsFilter[i1].filters.entityId.values = this.settings.kvsReferents.filter((item, i2) => i2 != i1).map(item => item.id);
      if (this.project.manager) {
        this.kvsReferentsFilter[i1].filters.entityId.values.push(this.project.manager.id);
      }
      this.reloadKvsReferentsRegister[i1] = this.reloadKvsReferentsRegister[i1] || ((reloadFn) => {
        this.reloadKvsReferents[i1] = reloadFn;
      });
    });
    this.reloadKvsReferents.forEach((reload, i1) => {
     if (i != i1) {
       reload();
     }
    });
  }

  private loadProjectFolder() {
    return this.restangular.one('/documents/folder/project').get().toPromise().then((folder) => {
      this.projectFolder = folder;
    });
  }

  onAdditionalInfoChanged() {
    if (!this.deregisterLeaveListener) {
      this.deregisterLeaveListener = this.transitionService.onBefore({}, this.beforeExit.bind(this));
    }
    this.additionalInfoChanged = true;
    if (this.APP_BRAND.NAME === 'RSD') {
      this.tabs.find(t => t.id === 'additional-info').required = true;
    }
  }

  private beforeExit(transition: Transition) {
    if (!this.additionalInfoChanged) {
      return;
    }
    return this.dialogService.confirmDialogPromise('Provedené úpravy nejsou uložené. Opravdu chcete opustit záložku bez uložení?')
      .then((data) => {
        if (data === true) {
          this.loadProperties();
          this.loadSettings();
          this.deregisterLeaveListener();
          this.deregisterLeaveListener = null;
          this.tabs.find(t => t.id === 'additional-info').required = false;
        } else {
          return Promise.resolve(false);
        }
      });
  }

  isValid() {
    return this.APP_BRAND.NAME !== 'RSD' || this.project.state !== 'STARTED' || (
      this.validatePayment(this.settings.paymentIsprofondGlobal)
      && this.validatePaymentInternalId(this.settings.jsivvInternalId)
    );
  }

  validatePayment(payment: any): boolean {
    return payment !== undefined && payment !== '' && StringUtils.valueToString(payment).length === 10;
  }

  validatePaymentInternalId(paymentInternalId: any): boolean {
    const ln = StringUtils.valueToString(paymentInternalId).length;
    return paymentInternalId !== undefined && paymentInternalId !== '' &&  ln > 3 && ln < 7;
  }

  async saveProperties() {
    if (!this.loaded) {
      return;
    }
    this.additionalInfoChanged = false;
    this.tabs.find(t => t.id === 'additional-info').required = false;
    await this.settingsService.settingsUpload(this.project.id, this.settings);
    this.projectProperties = await this.restangular.all('properties').customPOST(this.buildPropertiesPayload()).toPromise();
    await this.prepareProperties();
  }

  // odesílá pouze vyplněné hodnoty aktuálně viditelných bloků, takže skrytí/prázdní zpracovatelé/schvalovatelé se neuloží.
  private buildPropertiesPayload(): ProjectPropertiesGroupModel[] {
    return this.projectProperties.map(group => ({
      groupName: group.groupName,
      items: group.items
        .filter(item => item.type && this.hasValue(item) && this.isSectionVisible(group, item))
        .map(item => ({
          name: item.name,
          type: item.type,
          templateVariable: item.templateVariable,
          valueText: item.valueText,
          valueLong: item.valueLong,
          valueDate: item.valueDate,
          valueBoolean: item.valueBoolean,
        })),
    }));
  }

  isActiveTab(id: string) {
    return id === this.tab;
  }

  isActiveDocumentTab(id: string) {
    return id === this.documentTab;
  }

  uiOnParamsChanged(changedParams) {
    if (changedParams.tab !== undefined) {
      this.tab = changedParams.tab || 'notes';
    }
  }

  onUpdateNoteList() {
    const noteTab = this.tabs.find((tab) => tab.id === 'notes');
    noteTab.name = `Poznámky k ${this.APP_BRAND.NAME === 'RSD' ? 'akci' : 'projektu'} (${this.noteList.list.filter(a => !a.cancelled).length})`;
  }

  onUpdateHistoryList() {
    const historyTab = this.tabs.find((tab) => tab.id === 'history');
    historyTab.name = `Historie (${this.historyList.itemCount})`;
  }

  onUpdateLandTakeDocumentationNoteList() {
    const noteTab = this.tabs.find((tab) => tab.id === 'land-take-documentation-notes');
    noteTab.name = `Stav dat záborového elaborátu (${this.landTakeDocumentationNoteList.itemCount})`;
  }

  onUpdateAttachmentList() {
    const documentTab = this.tabs.find((tab) => tab.id === 'documents');
    documentTab.name = `Dokumenty (${this.attachmentList.list.filter(a => !a.cancelled).length})`;
  }

  onUpdateDocumentList() {
    const documentsCount = this.documentList.list.filter(d => !d.cancelled).length;
    const documentsTab = this.tabs.find((tab) => tab.id === 'documents-epd');
    documentsTab.name = `EPD (${documentsCount})`;

    const componentsCount = this.documentList.list
      .filter(d => !d.cancelled)
      .reduce((sum, item) => sum + item.components.filter(c => !c.cancelled).length, 0);
    const subDocumentsTab = this.documentTabs.find((tab) => tab.id === 'documents-epd__documents');
    const subComponentsTab = this.documentTabs.find((tab) => tab.id === 'documents-epd__components');

    subDocumentsTab.name = `Obsah dokumentů EPD (${documentsCount})`;
    subComponentsTab.name = `Seznam komponent EPD (${componentsCount})`;
  }

  onUserChanged(user: any, group: any, prefix: string) {
    if (user) {
      group.items.find(i => i.templateVariable === prefix).valueLong = user.id;

      let item = group.items.find(i => i.templateVariable === prefix + 'NazevSpolecnosti');
      if (item) {
        item.valueText = user.ssoCompanyName || (user.company ? user.company.name : '');
      }

      item = group.items.find(i => i.templateVariable === prefix + 'PrijmeniJmeno');
      if (item) {
        item.valueText = [user.name, user.surname].join(' ');
      }

      item = group.items.find(i => i.templateVariable === prefix + 'Email');
      if (item) {
        item.valueText = user.email;
      }
    }
  }

  isVisible(group, item): boolean {
    const sw = this.customGroupsVisibility.find(tv => item.templateVariable.indexOf(tv) !== -1);

    if (!sw || sw === item.templateVariable) {
      return true;
    }

    const switchItem = group.items.find(i => i.templateVariable === sw);

    return switchItem && switchItem.valueBoolean;
  }

  translate(text: string): string {
    if (text.indexOf('ŘSD')) {
      text = text.replace('ŘSD ČR', this.brandTranslatePipe.transform('ps.investorCommonName'));
    }

    return text;
  }

  private loadNotes() {
    this.noteList = this.listService.createList(
      'notes',
      {
        filters: {projectInfo: true, systemType: false, cancelled: false, },
        sortOrder: {sortBy: 'timestamp', direction: 'desc'}
      }
    );
    this.listService.fetchResult(this.noteList).then(() => {
      this.onUpdateNoteList();
    });
  }

  loadHistory() {
    if (!this.historyList) {
      this.historyList = this.listService.createList(
      'notes',
      {filters: {projectInfo: true, systemType: true}, sortOrder: {sortBy: 'timestamp', direction: 'desc'}}
      );
    }
    this.listService.fetchResult(this.historyList).then(() => {
      this.onUpdateHistoryList();
    });
  }

  private loadLandTakeDocumentationNotes() {
    this.landTakeDocumentationNoteList = this.listService.createList(
      'notes',
      {
        filters: {landTakeDocumentation: true, cancelled: false},
        sortOrder: {sortBy: 'timestamp', direction: 'desc'}
      }
    );
    this.listService.fetchResult(this.landTakeDocumentationNoteList).then(() => {
      this.onUpdateLandTakeDocumentationNoteList();
    });
  }

  private loadAttachments() {
    this.attachmentList = this.listService.createList(
      'attachments',
      {
        limit: null,
        filters: { projectInfo: true, cancelled: false },
        sortOrder: {sortBy: 'timestamp', direction: 'desc'}}
    );
    this.listService.fetchResult(this.attachmentList).then(() => {
      this.onUpdateAttachmentList();
    });
  }

  public loadDocuments() {
    this.documentList = this.listService.createList(
      'documents',
      { limit: null, filters: { folderId: (this.projectFolder ? this.projectFolder.id : -1), cancelled: false, classified: true, loadCollections: ['components', 'folder.relations'] }}
    );
    this.listService.fetchResult(this.documentList).then(() => {
      this.onUpdateDocumentList();
    });
  }

  private async loadAreas() {
    this.areas = await this.restangular
      .all('areas')
      .customPOST({ filter: { sortOrder: [{sortBy: 'name', direction: 'asc'}], limit: null, filters: {}}})
      .toPromise();
  }

  public onNewAssessment() {
    this.newPriceAssessment = { price: {} };
  }

  public onCancelNewAssessment() {
    this.newPriceAssessment = null;
  }

  public async onSaveAssessment() {
    const send = { ...this.newPriceAssessment };
    send.prices = Object.keys(send.price).map((key) => { return {areaId: key, price: send.price[key] }; });
    delete send.price;
    await this.restangular
      .all('price-assessments')
      .customPOST(send)
      .toPromise()
      .then(r => {
        this.onCancelNewAssessment();
        return this.loadAssessments();
      });
  }

  public async onUpdateAssessment() {
    const updating = this.assessments[this.assessments.length - 1];
    const send = { ...updating };
    send.prices = Object.keys(send.price).map((key) => { return { areaId: key, price: send.price[key] }; });
    delete send.priceOrig;
    delete send.price;
    await this.restangular
      .one('price-assessments', send.id)
      .customPUT(send)
      .toPromise()
      .then(r => {
        updating.price = <any> {};
        r.prices.forEach(pr => {
          updating.price[pr.areaId] = pr.price;
        });
        updating.priceOrig = { ...updating.price };
      });
  }

  public async loadAssessments() {
    await this.restangular
      .all('price-assessments').all('list')
      .customPOST()
      .toPromise()
      .then(l => {
        this.assessments = l;
        let i = 0;
        l.forEach(p => {
          p.price = <any> {};
          p.prices.forEach(pr => {
            p.price[pr.areaId] = pr.price;
          });
          if (++i == l.length) {
            p.priceOrig = { ...p.price };
          }
        });
      });
  }

  public isAssessmentValid(assessment) : boolean {
    return assessment.date &&
      assessment.number &&
      assessment.year &&
      isFinite(assessment.year) &&
      assessment.year % 1 == 0 &&
      Object.values(assessment.price).some(i => !!i) &&
      Object.values(assessment.price).every(i => !i || isFinite(<number>i));
  }

  private getTabs(): TabModel[] {
    const tabs = [{
        name: 'Poznámky k ' + (this.APP_BRAND.NAME === 'RSD' ? 'akci' : 'projektu'),
        id: 'notes',
        href: 'symap.project.info',
        urlParams: {tab : 'notes'},
      },
      {
        name: 'Stav dat záborového elaborátu',
        id: 'land-take-documentation-notes',
        href: 'symap.project.info',
        urlParams: {tab : 'land-take-documentation-notes'},
      },
      {
        name: 'Doplňkové údaje akce',
        id: 'additional-info',
        href: 'symap.project.info',
        urlParams: { tab : 'additional-info' },
      },
      {
        name: 'Historie',
        id: 'history',
        href: 'symap.project.info',
        urlParams: { tab: 'history' },
      },
      {
        name: 'Dokumenty',
        id: 'documents',
        href: 'symap.project.info',
        urlParams: {tab : 'documents'},
        muted: this.APP_BRAND.NAME === 'RSD',
      },
      {
        name: 'EPD',
        id: 'documents-epd',
        href: 'symap.project.info',
        urlParams: {tab : 'documents-epd'},
        hidden: this.APP_BRAND.NAME !== 'RSD',
      },
    ];

    // CR-4576 Funkce vypnuta
    if (false && !this.brandService.isVisible('mpv')) {
      tabs.splice(3, 0, {
        name: 'Výměry MF pro ceny za nájmy',
        id: 'price-assessment',
        href: 'symap.project.info',
        urlParams: {tab : 'price-assessment'},
      });
    }
    return tabs;
  }

  canEdit() {
    return this.authService.hasPermission('admin');
  }
}
